import { siteConfig } from '@afrilink/config';
import type { AuthVariant } from './AuthLayout';
import { TrustBlock } from './TrustBlock';
import { PlanRecapCard, type WizardPlanState } from './PlanRecapCard';

/**
 * AuthLeftPanel — desktop-only context column for AuthLayout.
 *
 * The 5 variants per BRD §5.3. Mobile collapses this away (see AuthLayout).
 *
 * Staff variant intentionally suppresses the TrustBlock + Hindsight affiliation
 * copy per ATL-7 — internal users don't need marketing, and the austere
 * presentation signals "this is an operational tool, not a sales surface".
 */

interface AuthLeftPanelProps {
  variant: AuthVariant;
  wizardPlan: WizardPlanState | null;
}

export function AuthLeftPanel({ variant, wizardPlan }: AuthLeftPanelProps) {
  return (
    <>
      <div>
        <div className="mb-10 font-display text-lg font-bold tracking-tight text-navy-primary">
          {siteConfig.name}
        </div>

        <PanelContent variant={variant} wizardPlan={wizardPlan} />
      </div>

      {variant === 'staff' ? (
        <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          Internal use only. All actions audited.
        </p>
      ) : (
        <TrustBlock variant="full" />
      )}
    </>
  );
}

function PanelContent({
  variant,
  wizardPlan,
}: {
  variant: AuthVariant;
  wizardPlan: WizardPlanState | null;
}) {
  switch (variant) {
    case 'wizard-return':
      // wizardPlan can be null if the sessionStorage carried a slug that no
      // longer maps to an active hostingPlans entry (rare; falls through to
      // marketing-direct copy).
      if (wizardPlan) {
        return (
          <div className="space-y-6">
            <h1 className="font-display text-3xl font-bold leading-tight tracking-tight text-navy-primary">
              You picked <span className="text-primary">{wizardPlan.name}</span>.
              <br />
              One step from going live.
            </h1>
            <PlanRecapCard plan={wizardPlan} />
            <p className="text-sm text-muted-foreground">
              Create your account on the right — we'll bring you back to finish
              your order. No payment yet.
            </p>
          </div>
        );
      }
      return <MarketingDirectContent />;

    case 'marketing-direct':
      return <MarketingDirectContent />;

    case 'password-recovery':
      return (
        <div className="space-y-4">
          <h1 className="font-display text-3xl font-bold leading-tight tracking-tight text-navy-primary">
            Reset your password
          </h1>
          <p className="text-sm text-muted-foreground">
            Choose a new password to regain access to your SightHost portal.
            Your cPanel password (if you have one) is separate and unchanged.
          </p>
        </div>
      );

    case 'invite-claim':
      return (
        <div className="space-y-4">
          <h1 className="font-display text-3xl font-bold leading-tight tracking-tight text-navy-primary">
            Welcome to SightHost
          </h1>
          <p className="text-sm text-muted-foreground">
            Set a password to claim your portal account. From here you'll
            manage your hosting, domains, and invoices in one place.
          </p>
        </div>
      );

    case 'lead-claim':
      // Phase 11 SP-21 — copy intentionally warmer than invite-claim because
      // these visitors have just had a sales conversation; they're not cold
      // web traffic. v0.2 §16.1 specifies the wording.
      return (
        <div className="space-y-4">
          <h1 className="font-display text-3xl font-bold leading-tight tracking-tight text-navy-primary">
            Welcome to SightHost
          </h1>
          <p className="text-sm text-muted-foreground">
            Your sales contact has prepared an account for you — let's finish
            setup. Choose a password below; we'll bring you straight into your
            dashboard.
          </p>
        </div>
      );

    case 'staff':
      return (
        <div className="space-y-4">
          <h1 className="font-display text-2xl font-bold leading-tight tracking-tight text-navy-primary">
            Staff sign-in
          </h1>
          <p className="text-sm text-muted-foreground">
            Two-factor authentication is required for admin sessions.
          </p>
        </div>
      );
  }
}

function MarketingDirectContent() {
  return (
    <div className="space-y-5">
      <h1 className="font-display text-3xl font-bold leading-tight tracking-tight text-navy-primary">
        Hosting + domains,
        <br />
        run by humans in
        <br />
        <span className="text-primary">South Africa</span>.
      </h1>
      <p className="text-sm leading-relaxed text-muted-foreground">
        Custom hosting platform built for South African businesses. Real-person
        support, transparent pricing, no long-term contracts. Sign in on the
        right.
      </p>
    </div>
  );
}
